import { Div } from '@codesuma/baseline'
import calendar, { IDayCell } from '../../services/calendar'
import settings, { CalendarSystem } from '../../services/settings'
import { dayMarks } from '../../services/day-holidays'
import { J_WEEKDAYS_SHORT, G_WEEKDAYS_SHORT, G_MONTHS_EN } from '../../configs/names'
import { monthColor } from '../../configs/colors'
import { toFaDigits } from '../../helpers/digits'
import dayColors from '../../services/day-colors'
import { paintDayColor } from '../../helpers/day-color'
import style from './index.module.css'

// One month of the year view: its name, a row of weekday initials and the whole
// month laid out 7 × 6 — every day number showing, which is the point of the
// page. Lead-in and lead-out days are left blank rather than dotted; at this
// size a dot is noise, and the twelve cards read as twelve blocks of days.
//
// The month stands straight on the page's surface, and the colours on it are
// the days' own — a day the reader gave a colour sits on a disc of it, as in
// the month grid. The colored theme is the exception: its calendar wears each
// month's own colour, so its cards do too.
export const MonthCard = (year: number, month: number, system: CalendarSystem) => {
    const base = Div()
    base.addClass(style.card)
    // Every card carries its month's colour; only the colored theme's rule
    // wears it (see .card), so switching theme needs no rebuild.
    base.el.style.setProperty('--month-color', `rgb(${monthColor(month, system)})`)

    const { weekStart } = settings.get()
    const info = calendar.monthAt(calendar.monthIndex(year, month, system), system)
    // The Persian week always begins on شنبه; the configurable first day is a
    // Gregorian-only preference, the same rule the month slide follows.
    const firstDay = system === 'gregorian' ? weekStart : 0
    const cells = calendar.grid(info, system, firstDay)

    // Gregorian months read left-to-right, so the grid columns flip with them.
    const grid = Div()
    grid.addClass(style.grid)
    if (system === 'gregorian') grid.attr('dir', 'ltr')

    const name = Div(system === 'gregorian' ? G_MONTHS_EN[month - 1] : info.name)
    name.addClass(style.cardName)
    // The way back to the calendar: the home slider's index for this month.
    name.attr('role', 'button')
    name.attr('tabindex', '0')
    name.el.onclick = () => base.emit('month-open', calendar.monthIndex(year, month, system))
    name.el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            name.el.click()
        }
    })
    base.append(name)

    const weekdayNames = system === 'gregorian' ? G_WEEKDAYS_SHORT : J_WEEKDAYS_SHORT
    for (let r = 0; r < 7; r++) {
        const label = Div(weekdayNames[(firstDay + r) % 7])
        label.addClass(style.cardWeekday)
        grid.append(label)
    }

    // 42 cells, always — a month that fits in five weeks still stands as tall as
    // one that needs six, so the twelve cards line up in their rows.
    cells.forEach((c: IDayCell, i: number) => {
        const cell = Div()
        cell.addClass(style.cardDay)
        grid.append(cell)
        if (!c.inMonth) return

        cell.text(system === 'gregorian' ? String(c.gd) : toFaDigits(c.jd))

        // The weekend follows the calendar on screen — جمعه alone in Jalaali,
        // Saturday and Sunday in Gregorian — and counts as a holiday alongside
        // the occasion holidays, exactly as it does in the month grid.
        const weekday = (firstDay + (i % 7)) % 7
        const isWeekend = system === 'gregorian' ? weekday <= 1 : weekday === 6
        // Which colour each side takes is one rule for the whole app, so this
        // card agrees with the month grid and the day page — see
        // services/day-holidays.ts.
        const { primary, other } = dayMarks(c, system, isWeekend)
        if (other) cell.addClass(style.cardHolidayOther)
        if (primary) cell.addClass(style.cardHoliday)
        if (c.isToday) cell.addClass(style.cardToday)

        // The day's own colour; the theme gives the shade (see
        // services/day-colors).
        const color = dayColors.colorOf(c)
        if (color) {
            cell.addClass(style.cardColored)
            paintDayColor(cell.el, color)
        }

        cell.attr('role', 'button')
        cell.attr('tabindex', '0')
        cell.attr('aria-label', system === 'gregorian'
            ? `${c.gd} ${G_MONTHS_EN[month - 1]}`
            : `${toFaDigits(c.jd)} ${info.name}`)
        cell.el.onclick = () => base.emit('day-open', c)
        cell.el.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                cell.el.click()
            }
        })
    })

    base.append(grid)

    return base
}
